import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { BarrelAttachment } from "../models/Barrel";
import {
	getBarrelAttachments,
	upsertBarrelAttachment,
	deleteBarrelAttachment,
	type UpsertBarrelAttachmentParams,
} from "../repositories/BarrelsRepository";
import { BARRELS_QUERY_KEYS } from "./useBarrels";

// ============================================
// Query Keys
// ============================================

export const BARREL_ATTACHMENTS_QUERY_KEYS = {
	all: ["barrelAttachments"] as const,
	byBarrelId: (barrelId: number) =>
		[...BARREL_ATTACHMENTS_QUERY_KEYS.all, barrelId] as const,
};

// ============================================
// useBarrelAttachments Hook (Query)
// ============================================

interface UseBarrelAttachmentsProps {
	barrelId: number | null;
	enabled?: boolean;
}

export const useBarrelAttachments = ({
	barrelId,
	enabled = true,
}: UseBarrelAttachmentsProps) => {
	return useQuery<BarrelAttachment[], Error>({
		queryKey: BARREL_ATTACHMENTS_QUERY_KEYS.byBarrelId(barrelId ?? 0),
		queryFn: () => getBarrelAttachments(barrelId!),
		enabled: enabled && barrelId != null,
		// The files card lives in a tab that unmounts on every switch; uploads and removals
		// invalidate the query.
		staleTime: 5 * 60 * 1000,
	});
};

// ============================================
// useBarrelAttachmentMutations Hook
// ============================================

interface MutationOptions {
	onUploadSuccess?: (data: BarrelAttachment) => void;
	onDeleteSuccess?: () => void;
	onError?: (error: Error) => void;
}

export const useBarrelAttachmentMutations = ({
	onUploadSuccess,
	onDeleteSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const uploadMutation = useMutation({
		mutationFn: async (params: UpsertBarrelAttachmentParams) => {
			return await upsertBarrelAttachment(params);
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: BARREL_ATTACHMENTS_QUERY_KEYS.byBarrelId(data.BarrelId),
			});
			queryClient.invalidateQueries({
				queryKey: BARRELS_QUERY_KEYS.all,
			});
			onUploadSuccess?.(data);
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	const deleteMutation = useMutation({
		mutationFn: async (barrelAttachmentId: number) => {
			await deleteBarrelAttachment(barrelAttachmentId);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: BARREL_ATTACHMENTS_QUERY_KEYS.all,
			});
			queryClient.invalidateQueries({
				queryKey: BARRELS_QUERY_KEYS.all,
			});
			onDeleteSuccess?.();
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		uploadAttachment: uploadMutation.mutate,
		uploadAttachmentAsync: uploadMutation.mutateAsync,
		isUploading: uploadMutation.isPending,
		deleteAttachment: deleteMutation.mutate,
		isDeleting: deleteMutation.isPending,
	};
};
